import { useMemo } from 'react'
import { useDataChange, useDataValue } from 'Simple/Data.js'
import { dateISOToDateAndTime } from 'Data/format.js'
import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import TysiaCheckbox from 'DesignSystem/TysiaCheckbox/index.js'
import TysiaCheckboxInput from 'DesignSystem/TysiaCheckbox/TysiaCheckboxInput/index.js'

import Charge from './Charge/index.js'
// Note: the views have to be inside Charge so that viewPath matches the code structure
// otherwise it throws an invalid-view error
import ActionsAction from './Charge/ActionsAction/index.js'
import Name from './Charge/Name/index.js'
import Price from './Charge/Price/index.js'
import InsuranceCode from './Charge/InsuranceCode/index.js'
import AffectsProduction from './Charge/AffectsProduction/index.js'
import IncludeInClaim from './Charge/IncludeInClaim/index.js'
import RequiresToothDetails from './Charge/RequiresToothDetails/index.js'
import View from './view.js'

export default function Logic(props) {
  let charges = useDataValue({
    context: 'charges',
    viewPath: props.viewPath,
  })
  let charge_ids = useDataValue({
    context: 'selected',
    path: 'charge_ids',
    viewPath: props.viewPath,
  })
  let change = useDataChange({
    context: 'selected',
    viewPath: props.viewPath,
  })
  let setFlowTo = useSetFlowTo(props.viewPath)

  let tableData = useMemo(
    () => ({
      columns: [
        {
          header: '',
          accessorKey: 'id',
          cell: cell => (
            <TysiaCheckbox
              onChange={value => {
                change(next => {
                  if (value) {
                    next.charge_ids.push(cell.getValue())
                  } else {
                    next.charge_ids = next.charge_ids.filter(
                      item => item !== cell.getValue()
                    )
                  }
                })
              }}
              value={charge_ids.includes(cell.getValue())}
              viewPath={props.viewPath}
            >
              {childProps => <TysiaCheckboxInput {...childProps} />}
            </TysiaCheckbox>
          ),
        },
        {
          header: 'Name',
          accessorKey: 'name',
          cell: cell => {
            return (
              <Name
                data={cell.row.original}
                viewPath={`${props.viewPath}/Charge(${cell.row.original.id})/Name`}
              />
            )
          },
        },
        {
          header: 'Price',
          accessorKey: 'price',
          cell: cell => (
            <Price
              data={cell.row.original}
              viewPath={`${props.viewPath}/Charge(${cell.row.original.id})/Price`}
            />
          ),
        },
        {
          header: 'Insurance code',
          accessorKey: 'insurance_code_id',
          cell: cell => (
            <InsuranceCode
              data={cell.row.original}
              viewPath={`${props.viewPath}/Charge(${cell.row.original.id})/InsuranceCode`}
            />
          ),
        },
        {
          header: 'Affects production',
          accessorKey: 'affecting_production_report',
          cell: cell => (
            <AffectsProduction
              data={cell.row.original}
              viewPath={`${props.viewPath}/Charge(${cell.row.original.id})/AffectsProduction`}
            />
          ),
        },
        {
          header: 'Include in claim',
          accessorKey: 'is_included_in_insurance_claim',
          cell: cell => (
            <IncludeInClaim
              data={cell.row.original}
              viewPath={`${props.viewPath}/Charge(${cell.row.original.id})/IncludeInClaim`}
            />
          ),
        },
        {
          header: 'Requires tooth details',
          accessorKey: 'requires_tooth_details',
          cell: cell => (
            <RequiresToothDetails
              data={cell.row.original}
              viewPath={`${props.viewPath}/Charge(${cell.row.original.id})/RequiresToothDetails`}
            />
          ),
        },
        {
          header: 'Last edit',
          accessorKey: 'sys_last_modified',
          cell: cell => dateISOToDateAndTime(cell.getValue()),
        },
        {
          header: '',
          accessorKey: 'organization_id',
          cell: cell => (
            <ActionsAction
              data={cell.row.original}
              viewPath={`${props.viewPath}/Charge(${cell.row.original.id})/ActionsAction`}
            />
          ),
        },
      ],
      data: charges ?? [],
    }),
    [charges, charge_ids, props.viewPath, change]
  )

  function onRowClick(row) {
    setFlowTo(
      normalizePath(
        props.viewPath,
        `Charge(${row.original.id})/ActionsAction/Edit/Content`
      )
    )
  }

  return (
    <View
      viewPath={props.viewPath}
      tableData={tableData}
      renderRow={Charge}
      getRowViewPath={({ data }) => `${props.viewPath}/Charge(${data.id})`}
      onRowClick={onRowClick}
    />
  )
}
